import React, { useEffect, useState, useMemo, forwardRef, useRef,
  useImperativeHandle } from "react";
import { useTranslation } from "../../../../../i18n/useTranslation";
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Keyboard,
  Pressable,
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import ShieldIcon from "../../../../../../../../assets/images/Shield.svg";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent, {
  personNameFloatingKeyboardProps,
} from "../../../common/FloatingTextInputComponent";
import {
  isPersonNameSegmentValid,
  sanitizeCityInput,
  sanitizeUsStateInput,
} from "../../../../../helper/personNameValidation";
import {
  ADDRESS_LINE_MAX_LENGTH,
  CITY_FIELD_MAX_LENGTH,
  hasRepeatingAddressSpecialCharacters,
  isAddressOrPostalContentValid,
  POSTAL_CODE_MAX_LENGTH,
  sanitizeAddressLineInput,
  sanitizePostalCodeInput,
  US_STATE_FIELD_MAX_LENGTH,
} from "../../../../../helper/addressFieldValidation";
import countryList from "../../../../../../../../apps/quickstage-bff-web/src/services/countryList.json";
import { searchLocationSuggestions, getPlaceDetails } from "../../../../../services/location.service";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  testID?: string;
};

// Mock address suggestions (replace with Google Places API in production)
const mockSuggestions = [
  { address: "2750 Paulina St.", city: "Chicago", state: "IL", postalCode: "60614" },
  { address: "West Chicago Avenue", city: "Chicago", state: "IL", postalCode: "60622" },
  { address: "Chicago Avenue", city: "Oak Park", state: "IL", postalCode: "60302" },
  { address: "Chicago Riverwalk", city: "Chicago", state: "IL", postalCode: "60601" },
  { address: "111 N High St", city: "Columbus", state: "OH", postalCode: "43215" },
];

export interface BillPayAddressStepHandle {
  submit: () => Promise<boolean>;
}

const BillPayAddressStep = forwardRef<BillPayAddressStepHandle, Props>(({ onValidationChange, testID }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
      senderAddress: "",
      senderCity: "",
      senderZipCode: "",
      senderState: "",
    });
  // display the field label from the gffpFieldsByCategory (from gffp response)
  const gffpFieldsByCategory = useSessionStore((s: SessionState) => (s as any).gffpFieldsByCategory);
  const senderInfoFields: any[] = (gffpFieldsByCategory?.["Sender Information"]) || [];
  const getFieldLabel = (tag: string): string | undefined => {
    const field = senderInfoFields.find((f: any) => (f.XmlTag ?? f.xmlTag) === tag);
    return field?.FieldLabel ?? field?.fieldLabel;
  };

  const addressPlaceholder = getFieldLabel("senderAddress") || t("ADDRESS");
  const cityPlaceholder = getFieldLabel("senderCity") || t("SENDER_ADDRESS_CITY_PLACEHOLDER");
  const postalPlaceholder = getFieldLabel("senderZipCode") || t("SENDER_ADDRESS_POSTAL_PLACEHOLDER");
  const statePlaceholder = getFieldLabel("senderState") || t("SENDER_ADDRESS_STATE_PLACEHOLDER");
  const [addressQuery, setAddressQuery] = useState("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const senderAddress = useSessionStore((s: SessionState) => (s as any).senderAddress) || "";
  const senderCity = useSessionStore((s: SessionState) => (s as any).senderCity) || "";
  const senderZipCode = useSessionStore((s: SessionState) => (s as any).senderZipCode) || "";
  const senderState = useSessionStore((s: SessionState) => (s as any).senderState) || "";
  const setSenderAddress = useSessionStore((s: SessionState) => (s as any).setSenderAddress);
  const setSenderCity = useSessionStore((s: SessionState) => (s as any).setSenderCity);
  const setSenderZipCode = useSessionStore((s: SessionState) => (s as any).setSenderZipCode);
  const setSenderState = useSessionStore((s: SessionState) => (s as any).setSenderState);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);
  const addressSuggestionsDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelDismissAddressSuggestions = () => {
    if (addressSuggestionsDismissTimerRef.current) {
      clearTimeout(addressSuggestionsDismissTimerRef.current);
      addressSuggestionsDismissTimerRef.current = null;
    }
  };

  const scheduleDismissAddressSuggestions = () => {
    cancelDismissAddressSuggestions();
    addressSuggestionsDismissTimerRef.current = setTimeout(() => {
      setShowSuggestions(false);
      addressSuggestionsDismissTimerRef.current = null;
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      cancelDismissAddressSuggestions();
    };
  }, []);

  // USA states sourced from countryList.json
  const usaStates = useMemo(() => {
    try {
      const usa = (countryList as any[]).find((c: any) => c?.CountryCode === "USA");
      return (usa?.States ?? []) as Array<{ stateid: number; stateAbbrivation: string; stateName: string }>;
    } catch {
      return [] as Array<{ stateid: number; stateAbbrivation: string; stateName: string }>;
    }
  }, []);

  const stateQuery = (senderState || "").trim().toUpperCase();
  const isExactAbbreviation = useMemo(() => {
    return usaStates.some((s) => (s.stateAbbrivation || "").toUpperCase() === stateQuery && stateQuery.length > 0);
  }, [usaStates, stateQuery]);

  const filteredStates = useMemo(() => {
    if (!stateQuery) return usaStates;
    return usaStates.filter((s) =>
      (s.stateName || "").toUpperCase().includes(stateQuery) || (s.stateAbbrivation || "").toUpperCase().includes(stateQuery)
    );
  }, [usaStates, stateQuery]);

  const isValid =
    senderAddress.trim().length > 0 &&
    isAddressOrPostalContentValid(senderAddress, ADDRESS_LINE_MAX_LENGTH) &&
    isPersonNameSegmentValid(senderCity, true, CITY_FIELD_MAX_LENGTH) &&
    senderZipCode.trim().length > 0 &&
    isAddressOrPostalContentValid(senderZipCode, POSTAL_CODE_MAX_LENGTH) &&
    senderState.trim().length === US_STATE_FIELD_MAX_LENGTH &&
    /^[A-Z]{2}$/.test(senderState.trim());

  // Clear all field errors once the form becomes valid (e.g. user filled all fields or selected a suggestion)
  useEffect(() => {
    if (isValid) {
      setErrors({
        senderAddress: "",
        senderCity: "",
        senderZipCode: "",
        senderState: "",
      });
    }
  }, [isValid]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // expose validate function
    useImperativeHandle(ref, () => ({
      submit: async () => {
        const newErrors = {
          senderAddress: !senderAddress.trim()
            ? "Address is required"
            : !isAddressOrPostalContentValid(senderAddress, ADDRESS_LINE_MAX_LENGTH)
              ? senderAddress.length > ADDRESS_LINE_MAX_LENGTH
                ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: ADDRESS_LINE_MAX_LENGTH })
                : hasRepeatingAddressSpecialCharacters(senderAddress)
                  ? t("ADDRESS_NO_REPEATING_SPECIAL_CHARS")
                  : t("ADDRESS_POSTAL_INVALID_CHARS")
              : "",
          senderCity: !senderCity.trim()
            ? "City is required"
            : !isPersonNameSegmentValid(senderCity, true, CITY_FIELD_MAX_LENGTH)
              ? senderCity.length > CITY_FIELD_MAX_LENGTH
                ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: CITY_FIELD_MAX_LENGTH })
                : t("PERSON_NAME_ERROR_LETTERS_ONLY")
              : "",
          senderZipCode: !senderZipCode.trim()
            ? "Postal code is required"
            : !isAddressOrPostalContentValid(senderZipCode, POSTAL_CODE_MAX_LENGTH)
              ? senderZipCode.length > POSTAL_CODE_MAX_LENGTH
                ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: POSTAL_CODE_MAX_LENGTH })
                : hasRepeatingAddressSpecialCharacters(senderZipCode)
                  ? t("ADDRESS_NO_REPEATING_SPECIAL_CHARS")
                  : t("ADDRESS_POSTAL_INVALID_CHARS")
              : "",
          senderState:
            senderState.trim().length === US_STATE_FIELD_MAX_LENGTH &&
            /^[A-Z]{2}$/.test(senderState.trim())
              ? ""
              : "State must be 2 letters (e.g., MN)",
        };
        setErrors(newErrors);
  
        // if any error string is non-empty
        const isValid = !Object.values(newErrors).some(Boolean);
        return isValid;
      },
    }));
  
  function getUSStateCode(region?: string, countryCode?: string): string {
    if (!region || countryCode !== "USA") return "";

    const usa = countryList.find((c) => c.CountryCode === "USA");
    if (!usa || !usa.States) return "";

    const state = usa.States.find(
      (s) => s.stateName.toLowerCase() === region.trim().toLowerCase()
    );

    return state?.stateAbbrivation ?? "";
  }

  //Address autocomplete API call
    // const handleAddressChange = async (text: string) => {
    //   setAddressQuery(text);
    //   setSenderAddress?.(text);
  
    //   if (text.length < 2) {
    //     setSuggestions([]);
    //     setShowSuggestions(false);
    //     return;
    //   }
  
    //   try {
    //     setLoading(true);
    //     const results = await searchLocationSuggestions(text);
    //     setSuggestions(results || []);
    //     setShowSuggestions(results && results?.length > 0);
    //   } catch (e) {
    //     console.error("Location suggestion error", e);
    //     setSuggestions([]);
    //     setShowSuggestions(false);
    //   } finally {
    //     setLoading(false);
    //   }
    // };
  const handleAddressChange = (text: string) => {
    const s = sanitizeAddressLineInput(text);
    setAddressQuery(s);
    setSenderAddress?.(s);
    if (errors.senderAddress) {
      setErrors((prev) => ({ ...prev, senderAddress: "" }));
    }

    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (s.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setLoading(false);
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      const currentRequestId = ++requestIdRef.current;

      try {
        setLoading(true);
        const results = await searchLocationSuggestions(s);

        //Ignore stale responses
        if (currentRequestId !== requestIdRef.current) return;

        setSuggestions(results || []);
        setShowSuggestions(!!results?.length);
      } catch (e) {
        if (currentRequestId !== requestIdRef.current) return;
        console.error("Location suggestion error", e);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, 300);
  };
  
    //Select suggestions
    const handleSelectSuggestion = async (item: any) => {
      cancelDismissAddressSuggestions();
      try {
        const place = await getPlaceDetails(item?.PlaceId);
        const rawAddr =
          `${place?.AddressNumber ?? ""} ${place?.Street ?? ""}`.trim() ||
          place?.Label?.slice(0, 30) ||
          "";
        setSenderAddress?.(sanitizeAddressLineInput(rawAddr));
        const rawCity =
          place?.Municipality?.slice(0, 20) || place?.Neighborhood?.slice(0, 20) || "";
        setSenderCity?.(sanitizeCityInput(rawCity, CITY_FIELD_MAX_LENGTH));
        setSenderZipCode?.(sanitizePostalCodeInput(place?.PostalCode || ""));
        const rawState =
          getUSStateCode(place?.Region, place?.Country) ||
          (place?.Region || "").slice(0, 2).toUpperCase();
        setSenderState?.(
          sanitizeUsStateInput(rawState).slice(0, US_STATE_FIELD_MAX_LENGTH)
        );
      } catch (e) {
        console.error("GetPlace error", e);
      }
  
      setShowSuggestions(false);
      Keyboard.dismiss();
    };
  

  return (
    <Pressable
      style={styles.container}
      testID={testID}
      onPress={() => {
        if (showSuggestions) {
          cancelDismissAddressSuggestions();
          setShowSuggestions(false);
          Keyboard.dismiss();
        }
      }}
    >
      <Text style={styles.title}>{t("SENDER_ADDRESS_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_ADDRESS_SUBTITLE")}</Text>
        
        <View style={styles.form}>
          {/* <FloatingTextInputComponent
            testID="billPayAddress"
            label={addressPlaceholder}
            value={senderAddress}
            onChangeText={(val) => {
              setSenderAddress?.(val);
              if (errors.senderAddress) setErrors({ ...errors, senderAddress: "" });
            }}
            errorText={errors.senderAddress}
          /> */}
          <FloatingTextInputComponent
            testID="billPayAddress"
            label={addressPlaceholder}
            value={senderAddress}
            isAutoFocus={false}
            maxLength={ADDRESS_LINE_MAX_LENGTH}
            onChangeText={handleAddressChange}
            errorText={errors.senderAddress}
            onClearError={() => setErrors((prev) => ({ ...prev, senderAddress: "" }))}
            autoComplete="street-address"
            onFocus={cancelDismissAddressSuggestions}
            onBlur={scheduleDismissAddressSuggestions}
          />
          {showSuggestions && (
          <View style={styles.dropdownContainer}>
            <FlatList
              keyboardShouldPersistTaps="handled"
              data={suggestions}
              keyExtractor={(item) => item?.PlaceId}
              renderItem={({ item }) => (
                <TouchableOpacity
                  testID="billPayAddresResult"
                  style={styles.suggestionItem}
                  onPressIn={cancelDismissAddressSuggestions}
                  onPress={() => handleSelectSuggestion(item)}
                >
                  <Text style={styles.suggestionText}>{item?.Text}</Text>
                </TouchableOpacity>
              )}
            />
          </View>
        )}
          <FloatingTextInputComponent
            {...personNameFloatingKeyboardProps}
            testID="billPayCity"
            label={cityPlaceholder}
            value={senderCity}
            maxLength={CITY_FIELD_MAX_LENGTH}
            onChangeText={(v) =>
              setSenderCity?.(sanitizeCityInput(v, CITY_FIELD_MAX_LENGTH))
            }
            errorText={errors.senderCity}
            onClearError={() => setErrors((prev) => ({ ...prev, senderCity: "" }))}
            autoComplete="postal-address-locality"
          />
          <FloatingTextInputComponent
            testID="billPayZipCode"
            label={postalPlaceholder}
            value={senderZipCode}
            maxLength={POSTAL_CODE_MAX_LENGTH}
            onChangeText={(v) => setSenderZipCode?.(sanitizePostalCodeInput(v))}
            errorText={errors.senderZipCode}
            onClearError={() => setErrors((prev) => ({ ...prev, senderZipCode: "" }))}
            autoComplete="postal-code"
          />
          <FloatingTextInputComponent
            {...personNameFloatingKeyboardProps}
            testID="billPayState"
            label={statePlaceholder}
            value={senderState}
            maxLength={US_STATE_FIELD_MAX_LENGTH}
            onChangeText={(v) =>
              setSenderState?.(
                sanitizeUsStateInput(v).slice(0, US_STATE_FIELD_MAX_LENGTH)
              )
            }
            errorText={errors.senderState}
            onClearError={() => setErrors((prev) => ({ ...prev, senderState: "" }))}
            autoComplete="postal-address-region"
          />

          {/* USA States dropdown with vertical scroll and search-as-you-type */}
          {!isExactAbbreviation && stateQuery.length > 0 && filteredStates.length > 0 && (
          <View style={styles.dropdownContainer}>
            <FlatList
              keyboardShouldPersistTaps="handled"
              data={filteredStates}
              keyExtractor={(item) => String(item.stateid)}
              renderItem={({ item }) => (
                <TouchableOpacity
                testID="billPayStateOption"
                  style={styles.suggestionItem}
                  onPress={() => {
                    try { setSenderState?.(item.stateAbbrivation); } catch {}
                    setShowSuggestions(false);
                    Keyboard.dismiss();
                  }}
                >
                  <Text style={styles.suggestionText}>{item.stateName} ({item.stateAbbrivation})</Text>
                </TouchableOpacity>
              )}
            />
          </View>
        )}
      </View>
      <View style={styles.hintBox}>
        <ShieldIcon width={24} height={24}/>
        <Text style={styles.hintText}>
          {t("YOUR_INFO_IS_ALWAYS_KEPT_PRIVATE")}
        </Text>
      </View>
    </Pressable>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
    overflow: "visible",
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  input: {
    backgroundColor: "#f5f5f5",
    padding: 12,
    borderRadius: 8,
    fontSize: 16,
    marginBottom: 10,
  },
  suggestionItem: { padding: 12, borderBottomWidth: 1, borderColor: "#eee" },
  suggestionText: { fontSize: 16 },
  form: {
    marginTop: 16,
    overflow: "visible",
    zIndex: 999,
    elevation: 20,
  },
  manualBtn: { marginTop: 16, alignItems: "center" },
  manualText: { fontSize: 16, fontWeight: "600" },
  hintBox: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: Colors.titleTextColor,
    borderRadius: 10,
    padding: 16,
    marginTop: 32,
    backgroundColor: Colors.white,
    marginBottom: 79
  },
  hintText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    fontWeight: '400', 
    color: Colors.darkGrey,
    lineHeight: 18,
    paddingLeft: 16
  },
  dropdownContainer: {
    maxHeight: 180,
    borderWidth: 1,
    borderColor: Colors.textInputBackground,
    borderRadius: 8,
    backgroundColor: Colors.white,
    marginTop: 4,
    position: "relative",
    zIndex: 9999,
    elevation: 30,
  },
});

export default BillPayAddressStep;